/**
 * The component either displays an animated thumbnail or a static one
 * depending if the game exists in the hardcoded ANIMATED_THUMBS array.
 * This is a temporary solution until the BE starts sending us the thumbnails.
 */

import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../../../../shared/components/Image';
import { useAssetsConfig } from 'store/app/appStore';

interface GameThumbnailProps {
  imageUrls: {
    main: string,
    gameKey: string,
    badgePlate: string | null,
    badge: string | null,
    orientation: 'vertical' | 'horizontal',
  };
  alt: string;
  loading?: 'lazy' | 'eager';
  style?: React.CSSProperties;
}

// Temporary until we start receiving the data from the BE
// Example: ANIMATED_THUMBS = ['BESlot', 'FXSFSSlot', 'TNBPSlot'];
const ANIMATED_THUMBS: string[] = [];

const GameThumbnail: React.FC<GameThumbnailProps> = ({
  imageUrls,
  alt,
  loading = 'lazy',
  style
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoLoadedRef = useRef(false);
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { assetsHost, assetsHash } = useAssetsConfig();

  const mainSrc = imageUrls.main;
  const animatedSrc = `/animated_thumbs/${imageUrls.gameKey}`;
  const gameHasAnimatedThumb = ANIMATED_THUMBS.includes(imageUrls.gameKey);

  const buildVideoUrl = (baseSrc: string, manualAffix = ".mp4") => {
    let videoUrl = '';

    if (assetsHost) {
      // When using CDN without hash (development), don't include hash in path
      const rootPath = assetsHash && assetsHash.trim() !== '' ? `/${assetsHash}/assets/portal/` : '/assets/portal';

      // Construct URL

      if (baseSrc) {
        if (!baseSrc.startsWith('/')) {
          // If src doesn't start with "/", use it directly
          videoUrl = baseSrc;
        } else {
          // If manual affix is provided, use it directly
          videoUrl = `${assetsHost}${rootPath}${baseSrc}${manualAffix}${assetsHash && assetsHash.trim() !== '' ? `?hash=${assetsHash}` : ''}`;
        }
      }
    }

    return videoUrl;
  };

  useEffect(() => {
    // Don't search for video file if the game doesn't exist in the ANIMATED_THUMBS array
    if (!gameHasAnimatedThumb) return;

    if (videoError) return;

    // Prevent video loading if assets host is not ready
    if (!assetsHost || assetsHost === '') return;
    // For CDN environments, wait for assetsHash to be available
    if (assetsHost.includes('cdn') && (!assetsHash || assetsHash === '')) return;

    // Reset states when imageUrls changes
    videoLoadedRef.current = false;
    setVideoError(false);
    setVideoLoaded(false);
    setShowVideo(false);

    const video = videoRef.current;
    if (!video) return;

    const cleanup = initializeVideo(video);
    return cleanup;
  }, [imageUrls, assetsHost, assetsHash]);

  const initializeVideo = (video: HTMLVideoElement) => {
    const handleCanPlay = () => {
      // Prevents re-execution
      if (videoLoadedRef.current) return;

      videoLoadedRef.current = true;

      // Postpone the initial switch to video by 1 second
      const showVideoDelay = setTimeout(() => {
        setVideoLoaded(true);
        setShowVideo(true);

        video.play().then(() => {
          // Remove the event listeners after the initial video play
          video.removeEventListener('canplay', handleCanPlay);
          video.removeEventListener('error', handleError);
        }).catch((error: unknown) => {
          console.warn('Autoplay blocked for animated thumbnail:', animatedSrc, error);
        });
      }, 1000);

      // Store the timeout for cleanup
      video.dataset['showVideoDelayId'] = String(showVideoDelay);
    };

    const handleError = () => {
      console.log('Video error:', animatedSrc);
      setVideoError(true);
      setShowVideo(false);
      // Also remove event listeners to prevent memory leaks
      cleanupEventListeners(video);
    };

    const cleanupEventListeners = (videoElement: HTMLVideoElement) => {
      videoElement.removeEventListener('canplay', handleCanPlay);
      videoElement.removeEventListener('error', handleError);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    // Set timeout to show fallback if video takes too long to load
    const loadTimeout = setTimeout(() => {
      // Use ref to check current state (avoiding closure staleness issue)
      if (!videoLoadedRef.current) {
        console.log('Video load timeout:', animatedSrc);
        setVideoError(true);
        setShowVideo(false);
      }
    }, 3000); // 3 second timeout before fallback

    // Set the video source directly, 
    video.src = buildVideoUrl(animatedSrc);
    video.load(); // Force the video to load the new source

    // Clean up event listeners
    // Clean up event listeners and timeout on unmount or when dependencies change
    return () => {
      cleanupEventListeners(video);
      clearTimeout(loadTimeout);
      // Clear the show video delay timeout
      const showVideoDelayId = video.dataset['showVideoDelayId'];
      if (showVideoDelayId) {
        clearTimeout(parseInt(showVideoDelayId, 10));
      }
    };
  };

  // If there was an error loading the video, show the regular image
  if (videoError) {
    console.log('Returning regular image due to video error:', mainSrc);
    return <Image src={mainSrc} alt={alt} loading={loading} style={style} />;
  }

  // Show video if loaded, otherwise show the regular image as fallback
  return (
    <>
      {/* Render video element if the game exists in the ANIMATED_THUMBS array */}
      {gameHasAnimatedThumb &&
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
            zIndex: -1, // iPhone fix
            opacity: (showVideo && videoLoaded) ? 1 : 0,
            // transition: 'opacity 0.5s ease'
          }}
          onError={() => {
            console.log('Video onError triggered:', animatedSrc);
            setVideoError(true);
            setShowVideo(false);
          }}
        >
          <source src={buildVideoUrl(animatedSrc)} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      }

      {/* Always render the image as fallback */}
      <Image
        src={mainSrc}
        alt={alt}
        loading={loading}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: 'var(--border-radius-for-game-cards, 5px)',
          opacity: (!videoError && showVideo && videoLoaded) ? 0 : 1,
          transition: 'opacity 0.5s ease',
          ...style
        }}
      />
    </>
  );
};

export default GameThumbnail;
